import { LogoLockupArt } from '@/components/LogoLockupArt';
import logoMark from '@/assets/logo-mark.png';

/**
 * The brand assets, in one place so every placement stays in step.
 *
 * Three variants, each with a job:
 *  • `<LogoLockup>`            — the "SelloKart" wordmark in its ink black, for
 *                                light surfaces. Needs room to be legible, so it
 *                                is for sign-in screens, not 32px chrome.
 *  • `<LogoLockup reversed />`  — the same wordmark with the neutral ink turned
 *                                white, for the dark brand panel on the sign-in
 *                                pages. The red barcode accent is brand colour
 *                                and is NOT inverted with it.
 *  • `<LogoMark>`              — the square "SK" app icon, for the sidebar and
 *                                mobile bar where the wordmark would be
 *                                unreadable and is already beside it as text.
 *
 * The two lockup variants are ONE piece of vector artwork, not two files:
 * `reversed` only picks the text colour that its `currentColor` ink resolves
 * against. They cannot drift apart, and there is no raster to upscale — the
 * previous PNGs were being magnified ~1.4x at the sign-in size and looked soft.
 *
 * CALLER BEWARE: size the lockup by WIDTH (`w-*` / `max-w-*`), not height. It is
 * roughly 7:1, so a height class turns into seven times as much width and
 * overflows its container almost immediately. See the note in `LogoLockup`.
 *
 * The mark is rendered inside a rounded, clipped tile so it reads as an app icon
 * rather than a stray image. The artwork carries its own corner radius and
 * transparent corners; the tile's `rounded-lg` is the tighter of the two at the
 * sizes used here, so it is the tile that shapes the silhouette.
 */

interface LogoProps {
  className?: string;
  /** White ink, for any dark surface. */
  reversed?: boolean;
}

export function LogoLockup({ className = 'w-56', reversed = false }: LogoProps) {
  // The colour class drives the artwork's `currentColor` ink. `brand-700` is the
  // logo's own #111111, so the light variant is the wordmark's true ink rather
  // than an approximation of it.
  //
  // `max-w-full h-auto` is not optional. Tailwind's preflight gives `img` a
  // `max-width:100%; height:auto`, but gives `svg` nothing — so when this was an
  // <img> the browser was silently capping the lockup to the container width,
  // and the `h-40`/`h-48` it was asked for never actually applied. Sizing by
  // height alone reintroduces that: this lockup is ~7:1, so every 1px of height
  // costs 7px of width and it blows out of the panel long before it looks tall.
  // Callers pass a WIDTH and let the height follow.
  return (
    <LogoLockupArt
      className={`${className} h-auto max-w-full select-none ${
        reversed ? 'text-white' : 'text-brand-700'
      }`}
    />
  );
}

export function LogoMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`${className} shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white`}
    >
      <img src={logoMark} alt="" className="h-full w-full object-cover" draggable={false} />
    </span>
  );
}
